import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Copy, 
  Star, 
  Download,
  Upload,
  Tag,
  Zap,
  Eye,
  X
} from 'lucide-react';

const PromptBank = () => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // NEW: For delete confirmation
  const [newPrompt, setNewPrompt] = useState({
    title: '',
    content: '',
    category: 'compliance',
    tags: '',
    label: '',
    is_public: false,
    is_favorite: false,
    approval_status: 'pending'
  });

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  const categories = [
    { id: 'all', label: 'All Prompts', icon: '📝' },
    { id: 'compliance', label: 'Compliance & Legal', icon: '⚖️' },
    { id: 'security', label: 'Security & Privacy', icon: '🔒' },
    { id: 'business', label: 'Business & Operations', icon: '💼' },
    { id: 'technical', label: 'Technical & Development', icon: '⚡' },
    { id: 'communication', label: 'Communication', icon: '💬' },
    { id: 'research', label: 'Research & Analysis', icon: '🔍' },
    { id: 'hr', label: 'HR & Training', icon: '👥' },
    { id: 'general', label: 'General', icon: '📄' }
  ];

  useEffect(() => {
    loadPrompts();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [prompts, searchTerm, selectedCategory, selectedLabel]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('personalityai_token') || localStorage.getItem('authToken');
      
      console.log('🔍 Debug: Loading prompts');
      console.log('🔍 Debug: Token exists:', !!token);
      console.log('🔍 Debug: API_BASE:', API_BASE);
      
      if (!token) {
        console.error('🔍 Debug: No token found for loading prompts');
        setPrompts([]);
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/prompts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Debug: Load prompts response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Debug: Load prompts data:', data);
        
        if (data.success) {
          setPrompts(data.prompts || []);
          console.log('🔍 Debug: Loaded prompts count:', data.prompts?.length || 0);
        } else {
          console.error('🔍 Debug: Failed to load prompts:', data.error);
          setPrompts([]);
        }
      } else {
        const errorText = await response.text();
        console.error('🔍 Debug: Load prompts HTTP error:', response.status, errorText);
        setPrompts([]);
      }
    } catch (error) {
      console.error('🔍 Debug: Error loading prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(prompt => prompt.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(prompt => 
        prompt.title.toLowerCase().includes(term) ||
        prompt.content.toLowerCase().includes(term) ||
        (prompt.tags && prompt.tags.toLowerCase().includes(term))
      );
    }

    // Filter by label
    if (selectedLabel) {
      filtered = filtered.filter(prompt => prompt.label === selectedLabel);
    }

    setFilteredPrompts(filtered);
  };

  const savePrompt = async () => {
    try {
      const token = localStorage.getItem('personalityai_token') || localStorage.getItem('authToken');
      
      // Debug logging
      console.log('🔍 Debug: Attempting to save prompt');
      console.log('🔍 Debug: Token exists:', !!token);
      console.log('🔍 Debug: Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
      console.log('🔍 Debug: Prompt data:', newPrompt);
      
      if (!token) {
        alert('No authentication token found. Please log in again.');
        return;
      }
      
      const isEditing = !!editingPrompt;
      
      const promptData = {
        ...newPrompt,
        tags: newPrompt.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      const url = isEditing 
        ? `${API_BASE}/api/prompts/${editingPrompt.id}`
        : `${API_BASE}/api/prompts`;
      
      const method = isEditing ? 'PUT' : 'POST';

      console.log('🔍 Debug: Making request to:', url);
      console.log('🔍 Debug: Method:', method);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(promptData)
      });

      console.log('🔍 Debug: Response status:', response.status);
      console.log('🔍 Debug: Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Debug: Response data:', data);
        
        if (data.success) {
          await loadPrompts(); // Reload prompts
          closeModal();
          alert('Prompt saved successfully!');
        } else {
          console.error('🔍 Debug: API returned error:', data.error);
          alert('Failed to save prompt: ' + data.error);
        }
      } else {
        const errorText = await response.text();
        console.error('🔍 Debug: HTTP error response:', errorText);
        alert(`Failed to save prompt (HTTP ${response.status}): ${errorText}`);
      }
    } catch (error) {
      console.error('🔍 Debug: Network error:', error);
      alert('Network error saving prompt: ' + error.message);
    }
  };

  // FIXED: Removed confirm() usage and used React state instead
  const deletePrompt = async (promptId) => {
    try {
      const token = localStorage.getItem('personalityai_token') || localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE}/api/prompts/${promptId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await loadPrompts(); // Reload prompts
        setShowDeleteConfirm(null); // Close confirmation modal
      } else {
        alert('Failed to delete prompt');
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Error deleting prompt');
    }
  };

  const toggleFavorite = async (promptId, currentFavorite) => {
    try {
      const token = localStorage.getItem('personalityai_token') || localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE}/api/prompts/${promptId}/favorite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_favorite: !currentFavorite })
      });

      if (response.ok) {
        await loadPrompts(); // Reload prompts
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    // Show toast notification
    const toast = document.createElement('div');
    toast.textContent = 'Prompt copied to clipboard!';
    toast.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #10b981; color: white; padding: 12px 20px; border-radius: 6px; z-index: 1000; font-size: 14px;';
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 2000);
  };

  const openEditModal = (prompt) => {
    setEditingPrompt(prompt);
    setNewPrompt({
      title: prompt.title,
      content: prompt.content,
      category: prompt.category,
      tags: Array.isArray(prompt.tags) ? prompt.tags.join(', ') : prompt.tags || '',
      label: prompt.label || '',
      is_public: prompt.is_public || false,
      is_favorite: prompt.is_favorite || false
    });
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingPrompt(null);
    setNewPrompt({
      title: '',
      content: '',
      category: 'general',
      tags: '',
      label: '',
      is_public: false,
      is_favorite: false
    });
  };

  // Compute unique labels for filter dropdown
  const uniqueLabels = Array.from(new Set(prompts.map(p => p.label).filter(Boolean)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading prompt bank...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">🏦 Prompt Bank</h1>
        <p className="opacity-90">Create, organize, and use your personal prompt library with PersonalityAI</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search and Filter */}
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
            <select
              value={selectedLabel || ''}
              onChange={e => setSelectedLabel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Labels</option>
              {uniqueLabels.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Prompt
            </button>
          </div>
        </div>
      </div>

      {/* Prompts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPrompts.map(prompt => (
          <div key={prompt.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 truncate">{prompt.title}</h3>
                    {prompt.is_favorite && (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      {categories.find(c => c.id === prompt.category)?.icon} {prompt.category}
                    </span>
                    {prompt.is_public && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Public</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-purple-600 bg-purple-100 rounded px-2 py-0.5">
                      {prompt.label || 'No Label'}
                    </span>
                    <span className="text-xs text-gray-400">{prompt.category}</span>
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => toggleFavorite(prompt.id, prompt.is_favorite)}
                    className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                  >
                    <Star className={`h-4 w-4 ${prompt.is_favorite ? 'fill-current text-yellow-500' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Content Preview */}
              <div className="mb-3">
                <p className="text-sm text-gray-600 line-clamp-3">
                  {prompt.content}
                </p>
              </div>

              {/* Tags */}
              {prompt.tags && prompt.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {(Array.isArray(prompt.tags) ? prompt.tags : prompt.tags.split(',')).slice(0, 3).map((tag, index) => (
                    <span key={index} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
                      {tag.trim()}
                    </span>
                  ))}
                  {(Array.isArray(prompt.tags) ? prompt.tags : prompt.tags.split(',')).length > 3 && (
                    <span className="text-xs text-gray-500">+{(Array.isArray(prompt.tags) ? prompt.tags : prompt.tags.split(',')).length - 3} more</span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(prompt.content)}
                    className="flex items-center text-xs text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                  <button
                    onClick={() => openEditModal(prompt)}
                    className="flex items-center text-xs text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </button>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(prompt.id)}
                  className="flex items-center text-xs text-gray-600 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredPrompts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedCategory !== 'all' 
              ? 'Try adjusting your search or filter'
              : 'Create your first prompt to get started'
            }
          </p>
          {!searchTerm && selectedCategory === 'all' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Prompt
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newPrompt.title}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter prompt title..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={newPrompt.content}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter your prompt content..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={newPrompt.category}
                      onChange={(e) => setNewPrompt(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {categories.filter(c => c.id !== 'all').map(category => (
                        <option key={category.id} value={category.id}>
                          {category.icon} {category.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <input
                      type="text"
                      value={newPrompt.tags}
                      onChange={(e) => setNewPrompt(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="tag1, tag2, tag3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                  <input
                    type="text"
                    value={newPrompt.label}
                    onChange={e => setNewPrompt(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g. Research, Work, Fun, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newPrompt.is_favorite}
                      onChange={(e) => setNewPrompt(prev => ({ ...prev, is_favorite: e.target.checked }))}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Add to favorites</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newPrompt.is_public}
                      onChange={(e) => setNewPrompt(prev => ({ ...prev, is_public: e.target.checked }))}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Make public (share with community)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={savePrompt}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {editingPrompt ? 'Update Prompt' : 'Create Prompt'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - NEW */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Delete Prompt
              </h3>
              
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this prompt? This action cannot be undone.
              </p>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deletePrompt(showDeleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptBank;
